import { Component } from '@theme/component';

const SECTION_NAME = 'esc-quick-add-modal';
const STYLE_ATTR = 'data-esc-quick-add-styles';

/** @type {Map<string, string>} */
const sectionCache = new Map();

/** @type {Map<string, Promise<string>>} */
const pendingSectionRequests = new Map();

/** @type {Promise<unknown[]> | null} */
let scriptsPromise = null;

/**
 * Loads custom elements used inside the fetched modal. Script tags in Section
 * Rendering HTML do not execute when injected via innerHTML.
 * @returns {Promise<unknown[]>}
 */
export function ensureQuickAddScripts() {
  if (!scriptsPromise) {
    scriptsPromise = Promise.all([
      import('@theme/product-carousel'),
      import('@theme/subscription-widget'),
      import('@theme/read-more'),
      import('@theme/local-pickup'),
    ]);
  }

  return scriptsPromise;
}

/**
 * @param {string} handle
 * @returns {Promise<string>}
 */
export async function fetchQuickAddSection(handle) {
  const cached = sectionCache.get(handle);
  if (cached) return cached;

  const pending = pendingSectionRequests.get(handle);
  if (pending) return pending;

  const request = fetch(`/products/${handle}?sections=${SECTION_NAME}`)
    .then((response) => {
      if (!response.ok) throw new Error('Failed to load quick add');
      return response.json();
    })
    .then((data) => {
      const html = data[SECTION_NAME];
      if (!html) throw new Error('Quick add section missing');
      sectionCache.set(handle, html);
      return html;
    })
    .finally(() => {
      pendingSectionRequests.delete(handle);
    });

  pendingSectionRequests.set(handle, request);
  return request;
}

/**
 * @param {string} html
 */
function injectQuickAddStyles(html) {
  if (document.querySelector(`style[${STYLE_ATTR}]`)) return;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const styles = [...doc.querySelectorAll('style')];
  if (!styles.length) return;

  const combined = document.createElement('style');
  combined.setAttribute(STYLE_ATTR, '');
  combined.textContent = styles.map((style) => style.textContent).join('\n');
  document.head.appendChild(combined);
}

/**
 * @param {string} html
 * @returns {InstanceType<typeof EscQuickAddModal>}
 */
export function createModalFromHtml(html) {
  injectQuickAddStyles(html);

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  const modal = wrapper.querySelector('esc-quick-add-modal');

  if (!modal) {
    throw new Error('Quick add modal missing');
  }

  return /** @type {InstanceType<typeof EscQuickAddModal>} */ (modal);
}

/**
 * @param {string} handle
 * @returns {Promise<InstanceType<typeof EscQuickAddModal>>}
 */
export async function loadQuickAddModal(handle) {
  await ensureQuickAddScripts();
  const html = await fetchQuickAddSection(handle);
  return createModalFromHtml(html);
}

class EscQuickAddModal extends Component {
  requiredRefs = ['modalContent'];

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {number} */
  #requestId = 0;

  /**
   * Preloads the section HTML for a product on hover so it's cached by click time.
   * @param {Event} event
   */
  preloadProduct(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const handle = target.dataset.productHandle;
    if (!handle || handle === this.dataset.productHandle) return;
    fetchQuickAddSection(handle);
  }

  /**
   * @param {Event} event
   */
  async switchProduct(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const handle = target.dataset.productHandle;

    if (!handle || handle === this.dataset.productHandle) return;

    const requestId = ++this.#requestId;
    this.#setLoading(true);

    try {
      const nextModal = await loadQuickAddModal(handle);
      if (requestId !== this.#requestId) return;

      const nextContent = nextModal.querySelector('.esc-quick-add-modal__content');
      const currentContent = this.querySelector('.esc-quick-add-modal__content');
      if (!nextContent || !currentContent) return;

      currentContent.replaceWith(nextContent);
      this.dataset.productId = nextModal.dataset.productId;
      this.dataset.productHandle = handle;
      this.updatedCallback();
    } catch (error) {
      if (requestId !== this.#requestId) return;
      window.location.href = `/products/${handle}`;
    } finally {
      if (requestId === this.#requestId) this.#setLoading(false);
    }
  }

  showModal() {
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      this.classList.remove('esc-quick-add-modal--hidden');
      window.dispatchEvent(new Event('resize'));
    });
  }

  hideModal() {
    this.#requestId += 1;
    this.#setLoading(false);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    this.classList.add('esc-quick-add-modal--hidden');
    this.remove();
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.classList.toggle('esc-quick-add-modal--loading', isLoading);
    this.toggleAttribute('aria-busy', isLoading);
  }
}

if (!customElements.get('esc-quick-add-modal')) {
  customElements.define('esc-quick-add-modal', EscQuickAddModal);
}
